import { Record, Map, List } from "immutable";
import { getRecordsByModel } from "common/selectors/idsManagement.selectors";
import { RootActions, RootState } from "common/reducers";
import { IdsManagementRecord, idsManagementFromJSON } from "common/records/IdsManagement";
import { metaPaginationFromJSON } from "common/records/MetaPagination";
import { IdType, Nullable } from "common/types";
import {
  CLEAR_PAGE,
  FETCH_MODEL,
  IdsManagementActions,
  ON_SORT_END,
  PREPEND_ID,
  PUSH_ID,
  PUSH_IDS,
  RECEIVE_MODEL,
  REMOVE_ID,
  REMOVE_IDS,
  REPLACE_ID,
  SET_BY_ATTR_PATH,
  SET_DIRTY_IDS,
  SET_MODEL_LOADED,
} from "common/reducers/idsManagement.actions";

export const IdsManagementStateRecord = Record({
  byModel: Map<string, IdsManagementRecord>(),
  dirtyIds: null as Nullable<List<IdType>>,
});

export const initialState = IdsManagementStateRecord();
export type IIdsManagementStateRecord = typeof initialState;

export const reducer = (state: RootState, action: RootActions): RootState => {
  const model = "idsManagement";
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case FETCH_MODEL: {
      const { modelName, queryParams } = action.payload;

      if (!state.getIn([model, "byModel", modelName])) {
        state = state.setIn([model, "byModel", modelName], idsManagementFromJSON({}));
      }

      state = state.setIn([model, "byModel", modelName, "loading"], true);
      state = state.setIn([model, "byModel", modelName, "queryParams"], queryParams);
      return state;
    }

    case RECEIVE_MODEL: {
      const { modelName, ids, metaPagination } = action.payload;

      if (!state.getIn([model, "byModel", modelName])) {
        state = state.setIn([model, "byModel", modelName], idsManagementFromJSON({}));
      }

      state = state.setIn([model, "byModel", modelName, "ids"], ids);
      state = state.setIn([model, "byModel", modelName, "loading"], false);

      if (metaPagination)
        state = state.setIn([model, "byModel", modelName, "metaPagination"], metaPaginationFromJSON(metaPagination));
      return state;
    }

    case PUSH_ID: {
      const { modelName, id } = action.payload;

      if (!state.getIn([model, "byModel", modelName])) {
        state = state.setIn([model, "byModel", modelName], idsManagementFromJSON({}));
      }

      return state.updateIn([model, "byModel", modelName, "ids"], (ids: List<IdType>) => {
        if (ids?.indexOf(id) < 0) return (ids || List()).push(id);
        return ids;
      });
    }

    case PUSH_IDS: {
      const { modelName, ids } = action.payload;

      return state.updateIn([model, "byModel", modelName, "ids"], (existingIds: List<IdType>) => {
        return List((existingIds || List()).concat(ids).toSet());
      });
    }

    case PREPEND_ID: {
      const { modelName, id } = action.payload;

      let existingIds = state.getIn([model, "byModel", modelName, "ids"]) as List<IdType>;
      if (!existingIds) {
        return state;
      }

      existingIds = existingIds.unshift(id);

      return state.setIn([model, "byModel", modelName, "ids"], existingIds);
    }

    case REPLACE_ID: {
      const { modelName, replacedId, newId } = action.payload;

      return state.updateIn([model, "byModel", modelName, "ids"], (ids: List<IdType>) => {
        const index = ids.indexOf(replacedId);
        if (index < 0) return ids;
        return ids.set(index, newId);
      });
    }

    case REMOVE_ID: {
      const { modelName, removeId } = action.payload;

      return state.updateIn([model, "byModel", modelName, "ids"], (ids: List<IdType>) => {
        const index = (ids || List()).indexOf(removeId);
        if (index < 0) return ids;
        return ids.delete(index);
      });
    }

    case REMOVE_IDS: {
      const { modelName, ids } = action.payload;

      return state.updateIn([model, "byModel", modelName, "ids"], (existingIds: List<IdType>) => {
        return List(existingIds.filter((id) => !ids.includes(id)).toSet());
      });
    }

    case SET_DIRTY_IDS:
      return state.setIn([model, "dirtyIds"], action.payload.ids);

    case SET_BY_ATTR_PATH: {
      const { modelName, id, attrPath, value, customPath } = action.payload;
      const path = customPath ? [...customPath, id, ...attrPath] : [modelName, "byId", id, ...attrPath];
      return state.setIn(path, value);
    }

    case ON_SORT_END:
      const { modelName, oldIndex, newIndex, attrPath, customPath } = action.payload;
      const path = customPath || [modelName, "byId"];

      const recordList = getRecordsByModel(state, { modelName, path });

      const moving = recordList.get(oldIndex);
      let dirtyIds = List<IdType>();

      const min = Math.min(oldIndex, newIndex);
      const max = Math.max(oldIndex, newIndex);
      const sorted = recordList.map((l) => l.getIn(attrPath)).sortBy((l) => l);

      const sortedIds = recordList
        .delete(oldIndex)
        .insert(newIndex, moving)
        .map((record, idx) => {
          const id = record.get("id");
          if (idx >= min && idx <= max) {
            dirtyIds = dirtyIds.push(id);
            state = reducer(
              state,
              IdsManagementActions.setByAttrPath(id, modelName, attrPath, sorted.get(idx), customPath),
            );
          }

          return id;
        });

      state = reducer(state, IdsManagementActions.setDirtyIds(dirtyIds.toSet().toList()));
      state = reducer(state, IdsManagementActions.receiveModel(modelName, sortedIds));

      return state;

    case SET_MODEL_LOADED: {
      const { modelName } = action.payload;
      return state.setIn([model, "byModel", modelName, "loading"], false);
    }

    case CLEAR_PAGE: {
      const { modelName } = action.payload;
      return state.setIn([model, "byModel", modelName], idsManagementFromJSON({}));
    }

    default:
      return state;
  }
};
